'use client';

import { createContext, type ReactNode, useContext } from 'react';
import { useCoordinatorShell } from 'src/components/Layout/PrivateLayout/CoordinatorShellContext';

/**
 * The 8px that closes the gap between the legacy shell and the design.
 *
 * The prototype's page padding is `--page-padding-x: 24px`
 * (`.content { padding: 0 var(--page-padding-x) 64px }`). Every settings surface
 * renders inside the app's shared `.ls-data-content`, which supplies 16px —
 * measured, `src/scss/layouts/_skeleton.scss`. That file is shared with many
 * non-settings pages and must not change, so the remaining 8px is added here:
 *
 *     legacy shell 16px  +  this 8px  =  the design's 24px
 *
 * It is NOT a magic number and it is not a settings-only preference; change the
 * shell's 16px and this has to change with it.
 *
 * Applied by `SettingsPage` (the page body), `SettingsBreadcrumb` (the band
 * above it, whose `.ls-inpage-header` supplies the same 16px), and
 * `SettingsPageHeader` behind an opt-in prop, for the one page that renders a
 * header outside a `SettingsPage`.
 *
 * KEEP THIS CONSTANT IN A .tsx FILE. Tailwind's scan is pinned to
 * `@source "../components/DS/**\/*.tsx"`, so the class name is only extracted
 * from a .tsx source. Move it to a .ts module and the utility stops being
 * generated, with no error anywhere.
 */
export const SETTINGS_PAGE_INSET = 'px-space-8';

/**
 * The design's page padding in full, for an element that inherits NONE of it.
 *
 * The two constants are not alternatives, they are two ways of reaching the same
 * 24px line, and which one an element needs depends only on what it sits inside:
 *
 *     inside `.ls-data-content` (16px)      ->  SETTINGS_PAGE_INSET   (+8)
 *     inside `.ls-global-header` (0px)      ->  SETTINGS_PAGE_PADDING_X (24)
 *
 * The second case is for a `SettingsTabStrip` rendered with `standalone` —
 * outside any `SettingsPage`, so it inherits none of the page's own inset.
 * Vendor Preferences no longer needs it: its market tabs render inside
 * `SettingsPage` (see `VendorPrioritization/index.tsx`), matching the Figma
 * frame's Page Header → Tabs → card stacking order, so they pick up the
 * page's own inset instead.
 */
export const SETTINGS_PAGE_PADDING_X = 'px-space-24';

/**
 * True inside a `SettingsPage`. Something that pulls itself back out to the pane's edge (the
 * save bar) needs to know whether the page's own 8px inset is around it or not, because the bar
 * is rendered both inside the page and as its sibling.
 */
const InsideSettingsPageContext = createContext(false);
export const useInsideSettingsPage = () => useContext(InsideSettingsPageContext);

type SettingsPageProps = {
  /** Page title/description block — normally a `<SettingsPageHeader />`. */
  header?: ReactNode;
  /** The page body: `SettingsSection`s and/or `SettingsRow`s. */
  children: ReactNode;
  /**
   * For a page whose body is one full-height pane rather than a scrolling column
   * of rows — the prototype's master-detail surfaces. The body claims the space
   * left over by the header, so a flex child inside it can own its own scrolling.
   * Vendor Preferences is the one page that needs it.
   */
  fill?: boolean;
  className?: string;
};

/**
 * Outer shell for a settings surface, matching the prototype's content column:
 * a page header, then the body, both running the full width of the column.
 *
 * NOTHING is bounded here. The prototype's content column is fluid
 * (`--content-width: 100%`) and only its settings row container is capped at
 * 1040px, so the cap lives on `SettingsRowGroup` — the element that IS that
 * container. Putting it here instead bounded the tab strip and every table too,
 * which is the bug this shape replaced. Tables fill the page.
 *
 * This component sets `data-lula-ds`, so anything rendered inside it is exempt
 * from the compatibility guards in `src/app/design-system-compat.css`. Wrap
 * whole converted pages in it; see DSSurface for the standalone marker.
 */
export const SettingsPage = ({ header, children, fill = false, className }: SettingsPageProps) => {
  const coordinatorShell = useCoordinatorShell();

  return (
    <InsideSettingsPageContext.Provider value>
      <div
        data-lula-ds=""
        className={`flex flex-col gap-space-16 font-sans ${
          coordinatorShell ? '' : SETTINGS_PAGE_INSET
        }${fill ? ' min-h-0 flex-1' : ''}${className ? ` ${className}` : ''}`}
      >
        {header}
        <div className={`flex w-full flex-col gap-space-16${fill ? ' min-h-0 flex-1' : ''}`}>
          {children}
        </div>
      </div>
    </InsideSettingsPageContext.Provider>
  );
};

export default SettingsPage;
